import React, { useState, useEffect } from 'react';
import { Box, Button, useTheme, useMediaQuery, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useCountryParam } from '../../common/util/useCountryParam';
import CustomizedTables from '../../common/Table';
import {
  listResidentialProperties,
  deleteResidentialProperty,
  listCommercialProperties,
  deleteCommercialProperty,
  listAgriculturalProperties,
  deleteAgriculturalProperty,
} from './PropertiesIndia.service';

const PropertiesIndia: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>('Residential');
  const country = useCountryParam();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Set property type from URL parameter on component mount
  useEffect(() => {
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl && ['Residential', 'Commercial', 'Agricultural'].includes(typeFromUrl)) {
      setSelectedPropertyType(typeFromUrl);
    }
  }, [searchParams]);

  // Function to handle property type change
  const handlePropertyTypeChange = (type: string) => {
    setSelectedPropertyType(type);
    navigate(`/properties?country=${country}&type=${type}`, { replace: true });
  };

  // Property type configurations with responsive columns
  const propertyTypeConfig: Record<string, any> = {
    Residential: {
      listService: listResidentialProperties,
      deleteService: deleteResidentialProperty,
      editLink: '/editPropertyIndia',
      columns: isMobile ? [
        { key: "title", label: "Title", minWidth: 120 },
        { key: "propertyType", label: "Type", minWidth: 80 },
        { key: "price", label: "Price (₹)", minWidth: 100 },
        { key: "area", label: "Area", minWidth: 80 },
        { key: "status", label: "Status", minWidth: 80 },
        { key: "propertyStatus", label: "Property Status", minWidth: 100 },
      ] : isTablet ? [
        { key: "title", label: "Title", minWidth: 150 },
        { key: "propertyType", label: "Property Type", minWidth: 130 },
        { key: "bhkType", label: "BHK Type", minWidth: 100 },
        { key: "price", label: "Price (₹)", minWidth: 130 },
        { key: "area", label: "Area (sq ft)", minWidth: 100 },
        { key: "address", label: "Location", minWidth: 150 },
        { key: "status", label: "Status", minWidth: 100 },
        { key: "propertyStatus", label: "Property Status", minWidth: 120 },
      ] : [
        { key: "title", label: "Title", minWidth: 150 },
        { key: "propertyType", label: "Property Type", minWidth: 130 },
        { key: "bhkType", label: "BHK Type", minWidth: 100 },
        { key: "price", label: "Price (₹)", minWidth: 130 },
        { key: "area", label: "Area (sq ft)", minWidth: 100 },
        { key: "address", label: "Location", minWidth: 200 },
        { key: "bedrooms", label: "Bedrooms", minWidth: 80 },
        { key: "bathrooms", label: "Bathrooms", minWidth: 80 },
        { key: "furnished", label: "Furnished", minWidth: 120 },
        { key: "images", label: "Images", minWidth: 120 },
        { key: "featured", label: "Featured", minWidth: 80 },
        { key: "status", label: "Status", minWidth: 100 },
        { key: "propertyStatus", label: "Property Status", minWidth: 120 },
      ]
    },
    Commercial: {
      listService: listCommercialProperties,
      deleteService: deleteCommercialProperty,
      editLink: '/editPropertyIndia',
      columns: isMobile ? [
        { key: "title", label: "Title", minWidth: 120 },
        { key: "propertyType", label: "Type", minWidth: 80 },
        { key: "price", label: "Price (₹)", minWidth: 100 },
        { key: "area", label: "Area", minWidth: 80 },
        { key: "status", label: "Status", minWidth: 80 },
        { key: "propertyStatus", label: "Property Status", minWidth: 100 },
      ] : isTablet ? [
        { key: "title", label: "Title", minWidth: 150 },
        { key: "propertyType", label: "Property Type", minWidth: 130 },
        { key: "price", label: "Price (₹)", minWidth: 130 },
        { key: "area", label: "Area (sq ft)", minWidth: 100 },
        { key: "address", label: "Location", minWidth: 150 },
        { key: "status", label: "Status", minWidth: 100 },
        { key: "propertyStatus", label: "Property Status", minWidth: 120 },
      ] : [
        { key: "title", label: "Title", minWidth: 150 },
        { key: "propertyType", label: "Property Type", minWidth: 130 },
        { key: "price", label: "Price (₹)", minWidth: 130 },
        { key: "area", label: "Area (sq ft)", minWidth: 100 },
        { key: "address", label: "Location", minWidth: 200 },
        { key: "floorNumber", label: "Floor", minWidth: 80 },
        { key: "furnished", label: "Furnished", minWidth: 120 },
        { key: "images", label: "Images", minWidth: 120 },
        { key: "featured", label: "Featured", minWidth: 80 },
        { key: "status", label: "Status", minWidth: 100 },
        { key: "propertyStatus", label: "Property Status", minWidth: 120 },
      ]
    },
    Agricultural: {
      listService: listAgriculturalProperties,
      deleteService: deleteAgriculturalProperty,
      editLink: '/editPropertyIndia',
      columns: isMobile ? [
        { key: "title", label: "Title", minWidth: 120 },
        { key: "price", label: "Price (₹)", minWidth: 100 },
        { key: "area", label: "Area", minWidth: 80 },
        { key: "status", label: "Status", minWidth: 80 },
        { key: "propertyStatus", label: "Property Status", minWidth: 100 },
      ] : isTablet ? [
        { key: "title", label: "Title", minWidth: 150 },
        { key: "price", label: "Price (₹)", minWidth: 130 },
        { key: "area", label: "Area", minWidth: 120 },
        { key: "address", label: "Location", minWidth: 150 },
        { key: "status", label: "Status", minWidth: 100 },
        { key: "propertyStatus", label: "Property Status", minWidth: 120 },
      ] : [
        { key: "title", label: "Title", minWidth: 150 },
        { key: "price", label: "Price (₹)", minWidth: 130 },
        { key: "area", label: "Area", minWidth: 120 },
        { key: "address", label: "Location", minWidth: 200 },
        { key: "postedBy", label: "Posted By", minWidth: 100 },
        { key: "images", label: "Images", minWidth: 120 },
        { key: "featured", label: "Featured", minWidth: 80 },
        { key: "status", label: "Status", minWidth: 100 },
        { key: "propertyStatus", label: "Property Status", minWidth: 120 },
      ]
    }
  };

  const currentConfig = propertyTypeConfig[selectedPropertyType];

  return (
    <Box sx={{ 
      paddingTop: { xs: '16px', sm: '8px', md: '16px' }, // Reduced top padding since Navbar already handles spacing
      paddingBottom: { xs: 2, sm: 3, md: 4 }, 
      paddingX: { xs: 0.5, sm: 1, md: 2 }, // Reduced horizontal padding to maximize space
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'column', md: 'row' }, // Stack vertically on tablet for better space usage
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'stretch', md: 'center' }, 
        mb: { xs: 2, sm: 2, md: 3 },
        gap: { xs: 2, sm: 2, md: 1 }
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant={isMobile ? "h6" : isTablet ? "h6" : "h5"} 
            component="h2" 
            sx={{ 
              mb: { xs: 1.5, sm: 1.5, md: 1.5 },
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 600,
              lineHeight: 1.3,
              wordBreak: 'break-word'
            }}
            style={{marginTop:'50px'}}
          >
            Properties List - India ({selectedPropertyType})
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row', md: 'row' }} 
            spacing={{ xs: 1, sm: 1, md: 1.5 }}
            sx={{ 
              mt: { xs: 1, sm: 1, md: 1.5 },
              flexWrap: 'wrap',
              '& .MuiButton-root': {
                minWidth: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '0.8rem', md: '0.875rem' },
                padding: { xs: '8px 16px', sm: '6px 12px', md: '8px 16px' }
              }
            }}
          >
            <Button
              variant={selectedPropertyType === 'Residential' ? 'contained' : 'outlined'}
              onClick={() => handlePropertyTypeChange('Residential')}
              sx={{ 
                ...(selectedPropertyType === 'Residential' ? {
                  backgroundColor: "#10374A",
                  "&:hover": {
                    backgroundColor: "#08293D",
                  },
                } : {
                  borderColor: "#10374A",
                  color: "#10374A",
                  "&:hover": {
                    borderColor: "#08293D",
                    color: "#08293D",
                    backgroundColor: "rgba(16, 55, 74, 0.04)",
                  },
                })
              }}
            >
              Residential
            </Button>
            <Button
              variant={selectedPropertyType === 'Commercial' ? 'contained' : 'outlined'}
              onClick={() => handlePropertyTypeChange('Commercial')}
              sx={{ 
                ...(selectedPropertyType === 'Commercial' ? {
                  backgroundColor: "#10374A",
                  "&:hover": {
                    backgroundColor: "#08293D",
                  },
                } : {
                  borderColor: "#10374A",
                  color: "#10374A",
                  "&:hover": {
                    borderColor: "#08293D",
                    color: "#08293D",
                    backgroundColor: "rgba(16, 55, 74, 0.04)",
                  },
                })
              }}
            >
              Commercial
            </Button>
            <Button
              variant={selectedPropertyType === 'Agricultural' ? 'contained' : 'outlined'}
              onClick={() => handlePropertyTypeChange('Agricultural')}
              sx={{
                ...(selectedPropertyType === 'Agricultural' ? {
                  backgroundColor: "#10374A",
                  "&:hover": {
                    backgroundColor: "#08293D",
                  },
                } : {
                  borderColor: "#10374A",
                  color: "#10374A",
                  "&:hover": {
                    borderColor: "#08293D",
                    color: "#08293D",
                    backgroundColor: "rgba(16, 55, 74, 0.04)",
                  },
                })
              }}
            >
              Agricultural
            </Button>
          </Stack>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size={isMobile ? "medium" : "small"}
          startIcon={<Add />}
          sx={{
            backgroundColor: "#10374A",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#08293D",
            },
            height: { xs: '48px', sm: '40px', md: '44px' },
            minWidth: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.8rem', md: '0.875rem' },
            whiteSpace: 'nowrap',
            px: { xs: 2, sm: 1.5, md: 2 },
            alignSelf: { xs: 'stretch', sm: 'flex-start', md: 'center' }
          }}
          component={Link}
          to={`/addPropertyIndia?type=${selectedPropertyType}&country=${country}`}
        >
          {isMobile ? `Add ${selectedPropertyType}` : isTablet ? `Add ${selectedPropertyType}` : `Add New ${selectedPropertyType} Property`}
        </Button>
      </Box>

      <Box sx={{ 
        width: '100%',
        overflow: 'auto',
        '& .MuiTableContainer-root': {
          maxWidth: '100%',
          overflowX: 'auto'
        }
      }}>
        <CustomizedTables
          key={`${selectedPropertyType}-${isMobile}-${isTablet}`} // Force re-render when property type or screen size changes
          columns={currentConfig.columns}
          listService={currentConfig.listService}
          deleteService={currentConfig.deleteService}
          editLink={currentConfig.editLink}
          country={country}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      </Box>
    </Box>
  );
};

export default PropertiesIndia;